//panel para el manejo de la informacion de los proyectos de ecotech
import { useState, useEffect } from 'react';
import { 
    collection, query, where, doc, updateDoc, onSnapshot, Timestamp} from 'firebase/firestore';
import 'react-toastify/dist/ReactToastify.css';
import { db } from '../firebase/config'
import { PROYECTOS_COLLECTION } from '../utils/constants'
import { addBusinessDays} from '../utils/helpers'

export const EcotechDashboard = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchProjects = () => {
        setLoading(true);
        const q = query(
            collection(db, PROYECTOS_COLLECTION),
            where("proveedorNombre", "==", "Ecotech Ingenieria del Medio Ambiente"),
            where("estado", "==", "Activo")
        );
        
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            setProjects(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (error) => {
            console.error("Error fetching projects for Ecotech: ", error);
            setLoading(false);
        });
        
        return unsubscribe;
    };

    useEffect(() => {
        const unsubscribe = fetchProjects();
        return () => unsubscribe();
    }, []);

    return (
        <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Gestión de Proyectos Ecotech</h1>
            {loading ? (
                <p>Cargando proyectos de Ecotech...</p>
            ) : projects.length === 0 ? (
                <p className="text-center text-gray-500 mt-8">No hay proyectos de Ecotech activos.</p>
            ) : (
                <EcotechProjectsTable projects={projects} onUpdateProject={fetchProjects} />
            )}
        </div>
    );
};

const EcotechProjectsTable = ({ projects, onUpdateProject }) => {
    const [modalProject, setModalProject] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const getProjectDisplayStatus = (project) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (project.datosEcotech?.estatus === 'Terminado') {
            return { text: 'Terminado', class: 'bg-blue-100 text-blue-800' };
        }

        if (project.datosEcotech?.fechaEnvioDigital?.toDate) {
            const deadlineLab = addBusinessDays(project.datosEcotech?.fechaEnvioDigital.toDate(), 15);
            if (today > deadlineLab) {
                return { text: 'Vencido Lab.', class: 'bg-red-100 text-red-800 font-bold' };
            }
            return { text: project.datosEcotech?.estatus, class: 'bg-green-100 text-green-800' };
        }

        if (project.datosEcotech?.fechaMuestreo?.toDate) {
            const deadlineInternal = addBusinessDays(project.datosEcotech?.fechaMuestreo.toDate(), 3);
            if (today > deadlineInternal) {
                return { text: 'Vencido Internamente', class: 'bg-orange-100 text-orange-800 font-semibold' };
            }
            return { text: project.datosEcotech?.estatus, class: 'bg-green-100 text-green-800' };
        }

        return { text: project.datosEcotech?.estatus || 'Pendiente', class: 'bg-gray-100 text-gray-700' };
    };

    const ManageEcotechProjectModal = ({ project, onClose, onFinalized }) => {
        const [labProjectNumber, setLabProjectNumber] = useState(project.datosEcotech?.numeroProyecto || '');
        const [workPoints, setWorkPoints] = useState(project.datosEcotech?.puntosDeTrabajo || '');
        const [notes, setNotes] = useState(project.datosEcotech?.notas || '');
        const [guiaEnvio, setGuiaEnvio] = useState(project.datosEcotech?.numeroGuiaEnvio || '');
        const [guiaRegreso, setGuiaRegreso] = useState(project.datosEcotech?.numeroGuiaRegreso || '');
        const [fechaMuestreo, setFechaMuestreo] = useState(project.datosEcotech?.fechaMuestreo || '');
        const [loading, setLoading] = useState(false);
        const currentStatus = project.datosEcotech?.estatus || 'Pendiente';
        
        const handleUpdateStatus = async (updateData) => {
            setLoading(true);
            const projectRef = doc(db, PROYECTOS_COLLECTION, project.id);
            try {
                await updateDoc(projectRef, updateData);
                onFinalized();
                onClose();
            } catch (err) {
                alert("Error al actualizar el estado.");
                setLoading(false);
            }
        };

        const handleSaveChanges = async () => {
            setLoading(true);
            const projectRef = doc(db, PROYECTOS_COLLECTION, project.id);
            try {
                await updateDoc(projectRef, {
                    "datosEcotech.numeroProyecto": labProjectNumber,
                    "datosEcotech.puntosDeTrabajo": Number(workPoints) || 0,
                    "datosEcotech.notas": notes,
                    "datosEcotech.numeroGuiaEnvio": guiaEnvio,
                    "datosEcotech.numeroGuiaRegreso": guiaRegreso,
                    "datosEcotech.estatus": currentStatus
                });
                onFinalized();
            } catch (err) { alert("No se pudieron guardar los cambios."); }
            finally { setLoading(false); }
        };

        const handleStart = () => handleUpdateStatus({ "datosEcotech.estatus": 'Pend. No de proyecto' });
        const handleSaveSamplingDate = () => handleUpdateStatus({ "datosEcotech.estatus": 'En Proceso', "datosEcotech.fechaMuestreo": Timestamp.fromDate(new Date(fechaMuestreo)) });
        const handleSendDigital = () => handleUpdateStatus({ "datosEcotech.estatus": 'Enviado Dig.', "datosEcotech.fechaEnvioDigital": Timestamp.now() });
        const handleSendPhysical = () => handleUpdateStatus({ "datosEcotech.estatus": 'Enviado Físicamente', "datosEcotech.numeroGuiaEnvio": guiaEnvio });
        const handleFinishProject = () => handleUpdateStatus({ "datosEcotech.estatus": 'Terminado', "datosEcotech.numeroGuiaRegreso": guiaRegreso });

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
                    <h3 className="text-lg font-bold mb-2">Gestionar Proyecto Ecotech: {project.npu}</h3>
                    <p className="text-sm text-gray-500 mb-6">Estado actual: <span className="font-bold">{project.datosEcotech?.estatus || 'Pendiente'}</span></p>

                    {currentStatus === 'Pendiente' && (
                        <button onClick={handleStart} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg mb-4">Empezar Tarea</button>
                    )}

                    {currentStatus === 'Pend. No de proyecto' && (
                        <div className="p-4 border rounded-md bg-gray-50 mb-4">
                            <label className="block text-sm font-medium text-gray-700">Introduce la Fecha de Muestreo</label>
                            <input type="date" value={fechaMuestreo} onChange={e => setFechaMuestreo(e.target.value)} className="mt-1 block w-full px-3 py-2 border rounded-md"/>
                            <button onClick={handleSaveSamplingDate} disabled={!fechaMuestreo} className="w-full mt-3 bg-blue-600 text-white font-bold py-2 rounded-lg disabled:bg-gray-400">Guardar Fecha y Poner "En Proceso"</button>
                        </div>
                    )}
                    {currentStatus === 'En Proceso' && (
                        <button onClick={handleSendDigital} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg mb-4">Marcar como "Enviado Digitalmente"</button>
                    )}
                    {currentStatus === 'Enviado Dig.' && (
                         <div className="p-4 border rounded-md bg-gray-50 mb-4">
                            <label className="block text-sm font-medium">Introduce la Guía de Envío Físico</label>
                            <input type="text" value={guiaEnvio} onChange={e => setGuiaEnvio(e.target.value)} placeholder="Número de guía..." className="mt-1 block w-full px-3 py-2 border rounded-md"/>
                            <button onClick={handleSendPhysical} disabled={!guiaEnvio} className="w-full mt-3 bg-blue-600 text-white font-bold py-2 rounded-lg disabled:bg-gray-400">Marcar como "Enviado Físicamente"</button>
                        </div>
                    )}
                    {currentStatus === 'Enviado Físicamente' && (
                         <div className="p-4 border rounded-md bg-gray-50 mb-4">
                            <label className="block text-sm font-medium">Introduce la Guía de Regreso para Finalizar</label>
                            <input type="text" value={guiaRegreso} onChange={e => setGuiaRegreso(e.target.value)} placeholder="Número de guía..." className="mt-1 block w-full px-3 py-2 border rounded-md"/>
                            <button onClick={handleFinishProject} disabled={!guiaRegreso} className="w-full mt-3 bg-green-600 text-white font-bold py-2 rounded-lg disabled:bg-gray-400">Finalizar Proyecto</button>
                        </div>
                    )}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium">Número de Proyecto (Laboratorio)</label>
                            <input type="text" value={labProjectNumber} onChange={e => setLabProjectNumber(e.target.value)} className="mt-1 block w-full px-3 py-2 border rounded-md"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Puntos de Trabajo</label>
                            <input type="number" value={workPoints} onChange={e => setWorkPoints(e.target.value)} className="mt-1 block w-full px-3 py-2 border rounded-md"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Nº de Guía (Envío)</label>
                            <input type="text" value={guiaEnvio} onChange={e => setGuiaEnvio(e.target.value)} className="mt-1 block w-full px-3 py-2 border rounded-md"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Nº de Guía (Regreso)</label>
                            <input type="text" value={guiaRegreso} onChange={e => setGuiaRegreso(e.target.value)} className="mt-1 block w-full px-3 py-2 border rounded-md"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Notas</label>
                            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows="3" className="mt-1 block w-full px-3 py-2 border rounded-md"></textarea>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <button onClick={onClose} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded">Cerrar</button>
                        <button onClick={handleSaveChanges} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded">{loading ? 'Guardando...' : 'Guardar Cambios'}</button>
                    </div>
                </div>
            </div>
        );
    };
    
    const filteredProjects = projects.filter(project => 
        (project.npu && project.npu.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (project.clienteNombre && project.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (project.servicioNombre && project.servicioNombre.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProjects.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <>
            <div className="mb-4 flex flex-col md:flex-row justify-between items-center">
                <input 
                    type="text"
                    placeholder="Buscar por NPU, cliente o servicio..."
                    className="w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-md mb-2 md:mb-0"
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
                <div className="flex items-center">
                    <span className="text-sm mr-2">Mostrar:</span>
                    <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-2 py-1 border border-gray-300 rounded-md">
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                </div>
            </div>

            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">NPU</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nº Proyecto Lab.</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Puntos</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notas</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guía Envío</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guía Regreso</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estatus Interno</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {currentItems.map(project => {
                            const displayStatus = getProjectDisplayStatus(project);
                            return (
                                <tr key={project.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{project.npu}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{project.clienteNombre}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{project.servicioNombre}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{project.datosEcotech?.numeroProyecto || '---'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{project.datosEcotech?.puntosDeTrabajo || '---'}</td>
                                    <td className="px-6 py-4 text-sm text-gray-500" title={project.datosEcotech?.notas}>
                                        <p className="w-40 truncate">{project.datosEcotech?.notas || '---'}</p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{project.datosEcotech?.numeroGuiaEnvio || '---'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{project.datosEcotech?.numeroGuiaRegreso || '---'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${displayStatus.class}`}>
                                            {displayStatus.text}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button onClick={() => setModalProject(project)} className="text-indigo-600 hover:text-indigo-900">Gestionar</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-700">Página {currentPage} de {totalPages}</span>
                <div>
                    <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md bg-white mr-2 disabled:opacity-50">Anterior</button>
                    <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="px-3 py-1 border rounded-md bg-white disabled:opacity-50">Siguiente</button>
                </div>
            </div>
            
            {modalProject && <ManageEcotechProjectModal project={modalProject} onClose={() => setModalProject(null)} onFinalized={onUpdateProject} />}
        </>
    );
};